import { gql } from 'graphql-tag';

/**
 * GraphQL query for fetching current user's own places.
 * Returns full details since user is viewing their own data.
 */
export const MY_PLACES_QUERY = gql`
  query MyPlaces {
    myPlaces {
      places {
        type
        label
        lat
        lng
        formattedAddress
        city
        state
        country
        isPrivate
        geocodeConfidence
        sourceType
      }
      isOwner
    }
  }
`;

/**
 * GraphQL query for fetching another user's places (public view).
 * Returns privacy-filtered data based on viewer permissions.
 *
 * @param userId - Required userId for viewing another user's places
 */
export const USER_PLACES_QUERY = gql`
  query UserPlaces($userId: String!) {
    userPlaces(userId: $userId) {
      places {
        type
        label
        lat
        lng
        formattedAddress
        city
        state
        country
        isPrivate
        geocodeConfidence
        sourceType
      }
      isOwner
    }
  }
`;

/**
 * Place type enum matching portfolio location types
 * Values match backend PlaceType enum (lowercase)
 */
export type PlaceType = 'home' | 'school' | 'workplace' | 'city' | 'county' | 'state';

/**
 * Geocode confidence level
 */
export type GeocodeConfidence = 'exact' | 'city_level' | 'state_level' | 'failed';

/**
 * Place interface matching the GraphQL Place type
 */
export interface Place {
  type: PlaceType;
  label: string;
  lat: number | null;
  lng: number | null;
  formattedAddress: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  isPrivate: boolean;
  geocodeConfidence: GeocodeConfidence | null;
  sourceType: string | null;
}

/**
 * Response interface for the myPlaces query
 */
export interface MyPlacesQueryResult {
  myPlaces: {
    places: Place[];
    isOwner: boolean;
  };
}

// =============================================================================
// Program Places module — GraphQL operations against the Nexus backend
// =============================================================================
// The IN-PERSON facilitation module (Places/Program-Modules epic, Wave 3). Dark
// behind PROGRAM_MODULES + PLACES_V1. Distinct from the portfolio "My Places"
// feature above (which lives on the User) — these operate on a PROGRAM's canonical
// Place references + the participant's attendance ledger.
//
// Every resolver returns the GraphQLJSON scalar, so each op selects the field with
// NO sub-selection set; the whole JSON payload is typed client-side below. Mirrors
// the Nexus backend live on :3008. Inline gql only.
// =============================================================================

// ------- Queries -------

/**
 * placesModuleState(programId) — the server-derived nudge state for the module.
 * `deriveState` runs SERVER-side, so the FE nudge is a pure pass-through of `state`.
 */
export const PLACES_MODULE_STATE_QUERY = gql`
  query PlacesModuleState($programId: String!) {
    placesModuleState(programId: $programId)
  }
`;

/**
 * myPlaceAttendance(programId) — the participant's attendance ledger for this
 * program plus the program's referenced Places and the resolved policy.
 */
export const MY_PLACE_ATTENDANCE_QUERY = gql`
  query MyPlaceAttendance($programId: String!) {
    myPlaceAttendance(programId: $programId)
  }
`;

/**
 * placesAttendanceSummary(programId) — the CREATOR-lens rollup (Places/Program-Modules
 * epic, Wave 4 / PW4). Returns the GraphQLJSON scalar (NO sub-selection). Per-participant
 * pledge-vs-this-week-actual-vs-status plus a cohort summary.
 *
 * The usePlaces loader SILENT-DEGRADES to an empty summary on error so the dashboard
 * renders its graceful-empty state.
 */
export const PLACES_ATTENDANCE_SUMMARY_QUERY = gql`
  query PlacesAttendanceSummary($programId: String!) {
    placesAttendanceSummary(programId: $programId)
  }
`;

/**
 * placesPresenceRoster(programId) — the co-presence "who's here" surface (PW6). Returns the
 * people ON SITE right now for this program (the BE scopes it: excludes the caller + anyone
 * opted out, and returns only co-citizens it's already safe to surface — no client-side
 * filtering needed). `recentCount` is "on-site today"; `total` is the roster the count is
 * drawn from. Returns the GraphQLJSON scalar (NO sub-selection), typed client-side below.
 *
 * The usePlaces loader SILENT-DEGRADES to a disabled ({ enabled:false, here:[] }) payload on
 * error so a failed fetch renders the graceful-empty / hidden state.
 */
export const PLACES_PRESENCE_ROSTER_QUERY = gql`
  query PlacesPresenceRoster($programId: String!) {
    placesPresenceRoster(programId: $programId)
  }
`;

// ------- Mutations -------

export const PLACE_CHECK_IN_MUTATION = gql`
  mutation PlaceCheckIn(
    $programId: String!
    $placeId: String!
    $note: String
    $geoLat: Float
    $geoLng: Float
    $qrToken: String
  ) {
    checkIn(
      programId: $programId
      placeId: $placeId
      note: $note
      geoLat: $geoLat
      geoLng: $geoLng
      qrToken: $qrToken
    )
  }
`;

export const PLACE_CHECK_OUT_MUTATION = gql`
  mutation PlaceCheckOut($programId: String!, $placeId: String!) {
    checkOut(programId: $programId, placeId: $placeId)
  }
`;

/**
 * setPlacesPolicy(input) — the CREATOR's on-site policy write (PW1 contract). Persists
 * AutoPilot.modules.places for the program. Returns the GraphQLJSON scalar (NO
 * sub-selection), mirroring placesModuleState — the whole resolved module-state payload
 * comes back so the caller can re-prefill without a second query.
 */
export const SET_PLACES_POLICY_MUTATION = gql`
  mutation SetPlacesPolicy($input: SetPlacesPolicyInput!) {
    setPlacesPolicy(input: $input)
  }
`;

/**
 * propagatePlacesChange(programId, dryRun) — the blast-radius gate (PW2 contract). With
 * dryRun:true it returns `{ affectedCount, participants:[{userId, changes, sessionCount}] }`
 * WITHOUT writing, so the creator can confirm "this changes on-site expectations for N
 * participants". With dryRun:false it applies the change to enrolled participants. Returns
 * the GraphQLJSON scalar (NO sub-selection).
 */
export const PROPAGATE_PLACES_CHANGE_MUTATION = gql`
  mutation PropagatePlacesChange($programId: String!, $dryRun: Boolean!) {
    propagatePlacesChange(programId: $programId, dryRun: $dryRun)
  }
`;

// =============================================================================
// Participant pledge / contract — PW5 (the participant surfaces).
// =============================================================================
// The participant's SIDE of the on-site policy: they PLEDGE how much on-site time
// they'll commit (an amount + a shape), which the server materializes into a set of
// scheduled sessions (their contract). All ops return the GraphQLJSON scalar (NO
// sub-selection), typed client-side below.

/**
 * myPlacesContract(programId) — the participant's resolved on-site contract + their
 * raw pledge. `contract` carries the materialized sessions + this-week/target rollup;
 * `pledge` echoes what they committed (amount + shape) so the Manage dialog can prefill
 * "Change pledge". Returns the GraphQLJSON scalar (NO sub-selection).
 */
export const MY_PLACES_CONTRACT_QUERY = gql`
  query MyPlacesContract($programId: String!) {
    myPlacesContract(programId: $programId)
  }
`;

/**
 * myPlacesSchedulePreview(programId, availability?) — the SCHEDULE-FIRST preview (PW5
 * redesign). The server generates the concrete day-by-day session list the participant
 * will confirm (each { uid, start(ISO), end(ISO), location }), plus the target/committed
 * rollup and the resolved attendance policy (attendanceMode / pledgeRequired /
 * allowPledgeLater). `availability` is the participant's weekly intake grid (from their
 * StudentPortfolio.intake.availability), which SEEDS the generated schedule. Returns the
 * GraphQLJSON scalar (NO sub-selection), typed client-side below. Flag-off → { enabled:false }.
 */
export const MY_PLACES_SCHEDULE_PREVIEW_QUERY = gql`
  query MyPlacesSchedulePreview($programId: String!, $availability: JSON) {
    myPlacesSchedulePreview(programId: $programId, availability: $availability)
  }
`;

/**
 * setPlacesPledge(...) — the participant commits (or re-commits) their on-site pledge.
 * The FE builds `sessions[]` ({uid,start,end}) from the pledge (regular → weekly
 * recurrence within the term; blocks → the chosen blocks) and passes the raw shape +
 * pledged amount too, so the server can re-materialize authoritatively if it prefers.
 * Returns `{contract, mode, status, autoApproved, shortfall, meetsRequirement}` — the FE
 * AUTO-APPROVES (proceeds) when `meetsRequirement`/`autoApproved`, else WARN-AND-ALLOWs
 * unless `shortfall` is a hard block. Returns the GraphQLJSON scalar (NO sub-selection).
 */
export const SET_PLACES_PLEDGE_MUTATION = gql`
  mutation SetPlacesPledge(
    $programId: String!
    $mode: String
    $shape: String
    $pledgedHours: Int
    $pledgedTotalHours: Int
    $sessions: JSON
    $availability: JSON
  ) {
    setPlacesPledge(
      programId: $programId
      mode: $mode
      shape: $shape
      pledgedHours: $pledgedHours
      pledgedTotalHours: $pledgedTotalHours
      sessions: $sessions
      availability: $availability
    )
  }
`;

/**
 * switchToRemote(programId) — the participant opts OUT of on-site attendance (goes
 * remote). The server flips their contract to `mode:'remote'` (status → 'remote') and
 * cancels pending sessions. Returns the GraphQLJSON scalar (NO sub-selection).
 */
export const SWITCH_TO_REMOTE_MUTATION = gql`
  mutation SwitchToRemote($programId: String!) {
    switchToRemote(programId: $programId)
  }
`;

/**
 * syncMyPlacesCalendar(programId) — push the participant's materialized sessions to
 * their connected calendar (best-effort). Returns `{sent, sessionCount, unchanged}` so
 * the Manage dialog can toast the outcome. Returns the GraphQLJSON scalar.
 */
export const SYNC_MY_PLACES_CALENDAR_MUTATION = gql`
  mutation SyncMyPlacesCalendar($programId: String!) {
    syncMyPlacesCalendar(programId: $programId)
  }
`;

/**
 * reportMissedSession(programId, sessionUid, status, note) — the participant reports a past
 * session they came LATE to or MISSED (PW6). This is the FIRST-CLASS report op that replaces
 * the PW5 stopgap (recording the report as a check-in note): it records the report AND clears
 * the stacking gate on that session. `status` is 'came_late' | 'missed'; `note` captures the
 * "what happened" for the missed case (optional). Returns the GraphQLJSON scalar.
 */
export const REPORT_MISSED_SESSION_MUTATION = gql`
  mutation ReportMissedSession(
    $programId: String!
    $sessionUid: String!
    $status: String!
    $note: String
  ) {
    reportMissedSession(
      programId: $programId
      sessionUid: $sessionUid
      status: $status
      note: $note
    )
  }
`;

/**
 * setPlacePresenceVisibility(programId, hidden) — the participant's "who's here" opt-out
 * (PW6). `hidden:true` removes them from every co-participant's presence roster for this
 * program; `hidden:false` (default) shows them. Returns the GraphQLJSON scalar.
 */
export const SET_PLACE_PRESENCE_VISIBILITY_MUTATION = gql`
  mutation SetPlacePresenceVisibility($programId: String!, $hidden: Boolean!) {
    setPlacePresenceVisibility(programId: $programId, hidden: $hidden)
  }
`;

// ------- Client-side shapes (mirror the backend JSON payloads) -------

/** A person tied to a place (arrival contact or on-site supervisor). */
export interface PlaceContact {
  name: string | null;
  role: string | null;
  phone: string | null;
  email: string | null;
}

/**
 * A canonical Place referenced by a program. All facilitation fields are OPTIONAL
 * (the BE contract) — a bare place is just a name + address.
 */
export interface ProgramPlace {
  id: string;
  name: string | null;
  formattedAddress: string | null;
  lat: number | null;
  lng: number | null;
  city: string | null;
  state: string | null;
  arrivalInstructions: string | null;
  contacts: PlaceContact[] | null;
  supervisors: PlaceContact[] | null;
  /** Free-form or structured operating hours (display-only). */
  operatingHours: unknown;
  photoUrls: string[] | null;
  deviceId: string | null;
  capacity: number | null;
  [k: string]: unknown;
}

/** One row of the participant's attendance ledger. */
export interface PlaceAttendanceRow {
  placeId: string;
  checkedInAt: string | null;
  checkedOutAt: string | null;
  status: string | null;
  scheduledWindowStart: string | null;
  scheduledWindowEnd: string | null;
  note: string | null;
}

/**
 * The resolved PlacesPolicy for a program (copies the CoachingPolicy pattern).
 * `schedule` is display-only (days/hours windows), shape left open.
 */
export interface PlacesPolicy {
  enabled: boolean;
  required?: boolean;
  rigidity?: 'soft' | 'hard';
  inAppNudge?: boolean;
  checkInRequired?: boolean;
  schedule?: unknown;
  /** The on-site presence target (e.g. 6 hours/week) — echoes the creator policy. */
  // eslint-disable-next-line no-use-before-define
  target?: PlacesTarget;
  /** Allowed on-site window (days + hours) — narrows the pledge availability grid. */
  // eslint-disable-next-line no-use-before-define
  siteWindow?: PlacesSiteWindow;
  /** Block continue on shortfall (true) vs. warn-and-allow (false). */
  shortfallBlock?: boolean;
}

/** Server-derived nudge state. Mirrors the ModuleNudgeState union. */
export type PlacesModuleNudgeState = 'idle' | 'due' | 'overdue';

/** The window of time a check-in/out is currently owed for (null when nothing is owed). */
export interface PlaceOwedWindow {
  start: string | null;
  end: string | null;
}

/**
 * One un-reported missed/elapsed-pending session the participant still owes a "what
 * happened?" report on — the AUTHORITATIVE stacking-gate row (server-derived). Each row
 * carries a stable identity: `checkInId` (the seeded ledger row) plus `sessionUid` (the
 * pledge-session uid `reportMissedSession` keys off — the server resolves it back from the
 * owed window's start). `sessionUid` may be null if the window can't be tied to a pledge
 * session, in which case the popup falls back to the pledge-derived heuristic.
 */
export interface PlaceOwedSession {
  checkInId: string;
  sessionUid: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  status: string;
}

/** placesModuleState payload. */
export interface PlacesModuleState {
  enabled: boolean;
  policy: PlacesPolicy;
  state: PlacesModuleNudgeState;
  owedWindow: PlaceOwedWindow | null;
  /** The authoritative stacking gate: true when there is at least one owed session. */
  blocked?: boolean;
  /** The un-reported missed sessions driving `blocked` (source of truth for the popup). */
  owedSessions?: PlaceOwedSession[];
}

/** myPlaceAttendance payload. */
export interface MyPlaceAttendance {
  enabled: boolean;
  attendance: PlaceAttendanceRow[];
  places: ProgramPlace[];
  policy: PlacesPolicy;
}

// =============================================================================
// Creator policy write — setPlacesPolicy input (PW1 contract) + confirm dialog
// (propagatePlacesChange) + creator-lens rollup (placesAttendanceSummary).
// =============================================================================

/** How on-site presence is targeted. */
export type PlacesTargetType = 'hours_week' | 'days_week' | 'total';

/** The on-site presence target (e.g. 6 hours/week). */
export interface PlacesTarget {
  type: PlacesTargetType;
  value: number;
}

/**
 * A fixed must-attend block layered on top of the loose target (e.g. "Tuesdays 4–6pm").
 * `day` is 0–6 (Sun–Sat) for a recurring weekly block; `date` pins a one-off.
 */
export interface PlacesFixedBlock {
  label?: string | null;
  day?: number | null;
  date?: string | null;
  startHm?: string | null;
  endHm?: string | null;
}

/** The allowed on-site window (which days + hours the site is open for attendance). */
export interface PlacesSiteWindow {
  /** Day-of-week indices 0–6 (Sun–Sat). */
  days: number[];
  startHm: string;
  endHm: string;
  timeZone: string;
}

/** How a check-in is verified. */
export type PlacesVerificationMethod = 'self' | 'geo' | 'qr';

export interface PlacesVerification {
  method: PlacesVerificationMethod;
  /** Geo-fence radius in meters (only meaningful for method:'geo'). */
  radiusM?: number | null;
}

/** One penalty threshold row (e.g. "<60% attendance → flagged"). */
export interface PlacesPenaltyThreshold {
  atPct: number;
  consequence: string;
}

export interface PlacesPenalty {
  thresholds: PlacesPenaltyThreshold[];
}

/** Reminder channels + how many hours ahead of an owed window to nudge. */
export interface PlacesReminders {
  inApp?: boolean;
  sms?: boolean;
  leadHours: number[];
}

/** A person tied to the on-site policy (arrival contact or supervisor). */
export interface PlacesPolicyPerson {
  name?: string | null;
  role?: string | null;
  phone?: string | null;
  email?: string | null;
}

/**
 * The setPlacesPolicy input (PW1 contract). Every facilitation field is OPTIONAL — a bare
 * enabled policy is valid. This same shape is reused (Partial) as the SponsorLink override
 * payload persisted at `programSequence[].placesOverride`.
 */
export interface SetPlacesPolicyInput {
  programId: string;
  enabled: boolean;
  // Flat to match the BE SetPlacesPolicyInput (a nested `target` object 400s;
  // the service re-nests into config.target = { type, value } on save).
  targetType?: PlacesTarget['type'];
  targetValue?: number;
  fixedBlocks?: PlacesFixedBlock[];
  siteWindow?: PlacesSiteWindow;
  rigidity?: 'soft' | 'hard';
  /** Require an explicit check-in (vs. passive presence). */
  checkInRequired?: boolean;
  /** Block enrollment on shortfall (true) vs. just warn (false). */
  shortfallBlock?: boolean;
  contact?: PlacesPolicyPerson;
  supervisor?: PlacesPolicyPerson;
  /** Free-form: what to do / bring / how to get there. */
  onSiteAim?: string;
  verification?: PlacesVerification;
  penalty?: PlacesPenalty;
  reminders?: PlacesReminders;
  /** How attendance may be fulfilled (default 'remote_allowed'). Flat field on the BE input. */
  attendanceMode?: 'in_person' | 'remote_allowed' | 'remote_only';
  /** Whether participants must set an in-person schedule before continuing (default true). */
  pledgeRequired?: boolean;
  /** Whether a required pledge may still be deferred ("set later") (default false). */
  allowPledgeLater?: boolean;
}

/**
 * The field-level SponsorLink override persisted at `programSequence[].placesOverride`.
 * Any present field overrides the program base; absent fields inherit. `enabled` toggles
 * the whole module for the link. Serialized as-is into the sponsor-settings JSON payload.
 */
export interface PlacesOverride {
  enabled?: boolean | null;
  target?: PlacesTarget | null;
  rigidity?: 'soft' | 'hard' | null;
  checkInRequired?: boolean | null;
  shortfallBlock?: boolean | null;
  siteWindow?: PlacesSiteWindow | null;
  verification?: PlacesVerification | null;
}

/** One participant's change row from propagatePlacesChange(dryRun). */
export interface PropagatePlacesParticipantChange {
  userId: string;
  /** Map of session/uid → the change kind. */
  changes: Record<string, 'create' | 'update' | 'cancel'>;
  sessionCount: number;
}

/** propagatePlacesChange payload (dryRun preview or applied result). */
export interface PropagatePlacesChangeResult {
  affectedCount: number;
  participants: PropagatePlacesParticipantChange[];
}

/** A participant's LIFECYCLE status on their own contract (myPlacesContract). */
export type PlacesContractStatus = 'active' | 'remote' | 'ended' | 'none';

/** A participant's STANDING on the creator rollup (placesAttendanceSummary). */
export type PlacesRollupStatus = 'on_pledge' | 'behind' | 'over' | 'remote' | 'unset';

/** One participant row of the creator-lens rollup (matches BE placesAttendanceSummary). */
export interface PlacesContractRow {
  userId: string;
  name: string | null;
  avatarUrl: string | null;
  /** Their pledge: shape + committed hours/week. */
  pledge: { shape: PlacesPledgeShape | null; hoursPerWeek: number | null } | null;
  /** Actual on-site hours logged this ISO week (site tz). */
  thisWeekHours: number | null;
  status: PlacesRollupStatus;
}

/** Cohort roll-up (matches BE). */
export interface PlacesContractCohort {
  count: number;
  avgPledgeHours: number | null;
  avgActualHours: number | null;
  pctPledgeMet: number | null;
  onTrack: number;
  behind: number;
  unset: number;
  remote: number;
}

/** placesAttendanceSummary payload (creator-lens rollup). */
export interface PlacesContractSummary {
  enabled: boolean;
  target: { type: string; value: number | null } | null;
  place: { name: string | null } | null;
  rows: PlacesContractRow[];
  cohort: PlacesContractCohort;
}

// =============================================================================
// Participant pledge / contract shapes (PW5)
// =============================================================================

/** How the participant commits their on-site time. */
export type PlacesPledgeMode = 'in_person' | 'remote';

/**
 * The SHAPE of a pledge — how the pledged time is distributed:
 *   - regular      → a fixed weekly pattern (painted on the availability grid)
 *   - week_by_week → they'll plan each week as it comes (loose)
 *   - blocks       → a few concrete date-blocks (e.g. an intensive week)
 *   - flexible     → no fixed pattern; just a total commitment
 */
export type PlacesPledgeShape = 'regular' | 'week_by_week' | 'blocks' | 'flexible';

/**
 * One materialized session sent to setPlacesPledge. `uid` is a client-stable id (so
 * the server can diff create/update/cancel); `start`/`end` are ISO timestamps.
 */
export interface PlacesPledgeSessionInput {
  uid: string;
  start: string;
  end: string;
}

/**
 * The participant's raw pledge (what they committed) — echoed back by myPlacesContract
 * so the Manage dialog can prefill "Change pledge".
 */
export interface PlacesPledge {
  mode: PlacesPledgeMode;
  shape: PlacesPledgeShape;
  /** Weekly commitment in hours (for regular / week_by_week / flexible). */
  pledgedHours: number | null;
  /** Total commitment in hours (for blocks / a whole-term total). */
  pledgedTotalHours: number | null;
  /** The painted weekly availability the regular pledge was built from. */
  availability?: Record<string, string[]> | null;
  /**
   * The uid-bearing sessions the pledge was materialized from (each carries the client-stable
   * `uid` that reportMissedSession needs). Prefer these over the resolved contract's sessions
   * (materialized fixedBlocks) for the stacking gate — the latter carry no uid.
   */
  sessions?: PlacesContractSession[] | null;
}

/** One materialized session on the resolved contract. */
export interface PlacesContractSession {
  uid: string;
  start: string | null;
  end: string | null;
  placeId?: string | null;
  status?: string | null;
}

/** The program term the contract's recurrence is bounded by. */
export interface PlacesContractTerm {
  start: string | null;
  end: string | null;
}

/**
 * The participant's resolved on-site contract. `sessions` are the materialized windows;
 * `weeklyTargetHours`/`target` echo what the program hopes for; `pledgedHours` is what
 * they committed; `status` is their standing against it.
 */
export interface PlacesContract {
  mode: PlacesPledgeMode;
  shape: PlacesPledgeShape;
  status: PlacesContractStatus;
  sessions: PlacesContractSession[];
  /** The program's on-site target (echoes the policy target value). */
  target: number | null;
  /** Weekly target in hours (the "we hope for ~N hrs/week" figure). */
  weeklyTargetHours: number | null;
  /** What the participant pledged (weekly hours). */
  pledgedHours: number | null;
  /** Total pledged hours (blocks / whole-term). */
  pledgedTotalHours: number | null;
  /** Hours actually logged THIS week (for the progress bar). */
  loggedHoursThisWeek?: number | null;
  /** The participant's "who's here" opt-out (true → hidden from the co-presence roster). */
  hidePresence?: boolean | null;
  term: PlacesContractTerm | null;
}

/** myPlacesContract payload. */
export interface MyPlacesContract {
  contract: PlacesContract | null;
  pledge: PlacesPledge | null;
}

/**
 * setPlacesPledge result. `autoApproved`/`meetsRequirement` → proceed silently;
 * a positive `shortfall` with `meetsRequirement:false` → warn-and-allow (or hard-block
 * if the policy's shortfallBlock is set, which the server encodes by refusing the write).
 */
export interface SetPlacesPledgeResult {
  contract: PlacesContract | null;
  mode: PlacesPledgeMode;
  status: PlacesContractStatus;
  autoApproved: boolean;
  /** True when the pledge falls under the hoped-for target (a hard-block shortfall). */
  shortfall: boolean;
  meetsRequirement: boolean;
}

/** How on-site attendance may be fulfilled (echoes the resolved policy). */
export type PlacesAttendanceMode = 'in_person' | 'remote_allowed' | 'remote_only';

/**
 * One generated session on the schedule-first preview. `location` is the resolved
 * site name/address for the row (display-only); `start`/`end` are ISO timestamps.
 */
export interface PlacesPreviewSession {
  uid: string;
  start: string;
  end: string;
  location: string | null;
}

/**
 * myPlacesSchedulePreview payload — the concrete generated schedule the participant edits
 * and confirms (PW5 schedule-first). `committedHours` is the weekly average implied by the
 * current `sessions`; `targetHours` is the program's hoped-for weekly hours. `attendanceMode`
 * / `pledgeRequired` / `allowPledgeLater` drive the remote toggle + the "set later" affordance.
 * Flag-off / disabled backend → { enabled:false }.
 */
export interface MyPlacesSchedulePreview {
  enabled: boolean;
  sessions: PlacesPreviewSession[];
  targetType: PlacesTargetType | string | null;
  targetValue: number | null;
  /** The program's hoped-for weekly hours. */
  targetHours: number | null;
  /** The weekly average implied by the current sessions. */
  committedHours: number | null;
  meetsTarget: boolean;
  /** Block continue on shortfall (true) vs. warn-and-allow (false). */
  shortfallBlock: boolean;
  attendanceMode: PlacesAttendanceMode;
  /** Whether the participant must set an in-person schedule before continuing. */
  pledgeRequired: boolean;
  /** Whether a required pledge may still be deferred ("set later"). */
  allowPledgeLater: boolean;
  /** The resolved site name/address (default location for added rows). */
  siteLocation: string | null;
}

/** The reason code on the setPlacesPledge typed envelope. */
export type SetPlacesPledgeReason =
  | 'saved'
  | 'saved_shortfall'
  | 'shortfall_blocked'
  | 'not_enrolled'
  | 'error';

/**
 * The setPlacesPledge TYPED ENVELOPE (PW5 redesign). Replaces the null-on-error result:
 * `ok` + `reason` let the caller surface the REAL outcome — `shortfall_blocked` (under the
 * required commitment), `not_enrolled` (no enrollment found), `error` (generic), or a
 * success (`saved` | `saved_shortfall`). `contract` echoes the resolved contract on success.
 */
export interface SetPlacesPledgeEnvelope {
  enabled: boolean;
  ok: boolean;
  reason: SetPlacesPledgeReason;
  contract: PlacesContract | null;
}

/** syncMyPlacesCalendar result. */
export interface SyncPlacesCalendarResult {
  sent: boolean;
  sessionCount: number;
  /** True when nothing changed since the last sync (idempotent no-op). */
  unchanged: boolean;
}

/** switchToRemote result (the re-resolved contract, remote mode). */
export interface SwitchToRemoteResult {
  contract: PlacesContract | null;
  mode: PlacesPledgeMode;
  status: PlacesContractStatus;
}

// =============================================================================
// Co-presence "who's here" + report-missed shapes (PW6)
// =============================================================================

/**
 * One co-present person on the "who's here" roster. The BE has already scoped this list
 * (excludes the caller + opted-out + anyone not safe to surface), so no client filtering is
 * applied — but the FE still never renders precise data from an empty/disabled roster.
 */
export interface PlacesPresencePerson {
  userId: string;
  name: string | null;
  avatarUrl: string | null;
  /** ISO timestamp of when they checked in on site. */
  checkedInAt: string | null;
  placeId: string | null;
}

/**
 * placesPresenceRoster payload — co-present people on-site now plus counts. `here` is who's
 * checked in RIGHT NOW; `recentCount` is "on-site today"; `total` is the roster size the
 * count is drawn from. `enabled:false` → render nothing / graceful-empty (never precise).
 */
export interface PlacesPresenceRoster {
  enabled: boolean;
  here: PlacesPresencePerson[];
  recentCount: number;
  total: number;
}

/** The report status for a past on-site session. */
export type ReportMissedStatus = 'came_late' | 'missed';

/** reportMissedSession result — echoes the cleared session + the participant's new standing. */
export interface ReportMissedSessionResult {
  sessionUid: string;
  status: ReportMissedStatus;
  /** True when the stacking gate for this session is now cleared. */
  cleared: boolean;
}

/** setPlacePresenceVisibility result — echoes the persisted opt-out flag. */
export interface SetPlacePresenceVisibilityResult {
  hidden: boolean;
}
